<script>
/**
 * 회원 목록 페이지.
 *
 * 기능:
 * - 회원 목록 조회 (서버 페이지네이션)
 * - 검색 필터: 키워드(이름·연락처·이메일) / 가입유형 / 탈퇴여부
 * - 회원 등록 버튼 → MemberCreateDialog 오픈
 * - 행 클릭 → /members/:userCode 상세 페이지 이동
 * - 공통 AppPagination 컴포넌트 재사용
 */
import { getMemberList } from '@/api/member.js'
import MemberCreateDialog from './MemberCreateDialog.vue'
import AppPagination from '@/components/common/AppPagination.vue'
import AdminShell from '@/components/admin/layout/AdminShell.vue'
import AdminFilterBar from '@/components/admin/filter/AdminFilterBar.vue'
import AdminDataTable from '@/components/admin/table/AdminDataTable.vue'
import AdminStatusBadge from '@/components/admin/table/AdminStatusBadge.vue'
import AdminLoadingState from '@/components/admin/feedback/AdminLoadingState.vue'
import AdminEmptyState from '@/components/admin/feedback/AdminEmptyState.vue'

export default {
  name: 'MemberListPage',

  components: {
    MemberCreateDialog,
    AppPagination,
    AdminShell,
    AdminFilterBar,
    AdminDataTable,
    AdminStatusBadge,
    AdminLoadingState,
    AdminEmptyState,
  },

  data() {
    return {
      /* ── 검색 조건 ── */
      search: {
        keyword: '',
        loginType: '',
        deleteYn: '',
      },

      /** 가입유형 select 옵션 */
      loginTypeOptions: [
        { title: '전체', value: '' },
        { title: '사이트 로그인', value: 'SITE' },
        { title: '소셜 로그인', value: 'OAUTH' },
      ],

      /** 상태 select 옵션 */
      deleteYnOptions: [
        { title: '전체', value: '' },
        { title: '정상', value: 'N' },
        { title: '탈퇴', value: 'Y' },
      ],

      /** 소셜 provider 표시용 레이블 맵 */
      providerLabelMap: {
        KAKAO: '카카오',
        NAVER: '네이버',
        GOOGLE: '구글',
      },

      /** 소셜 provider 칩 색상 맵 */
      providerColorMap: {
        KAKAO: 'warning',
        NAVER: 'success',
        GOOGLE: 'error',
      },

      /* ── 목록 데이터 ── */
      items: [],

      /* ── 페이지네이션 ── */
      page: 1,
      size: 10,
      total: 0,

      /* ── UI 상태 ── */
      loading: false,
      createDialog: false,
    }
  },

  mounted() {
    this.fetchList()
  },

  computed: {
    filterFields() {
      return [
        {
          key: 'keyword',
          type: 'text',
          label: '회원 검색',
          placeholder: '이름, 연락처, 이메일',
        },
        {
          key: 'loginType',
          type: 'select',
          label: '가입유형',
          options: this.loginTypeOptions,
          itemTitle: 'title',
          itemValue: 'value',
        },
        {
          key: 'deleteYn',
          type: 'select',
          label: '회원 상태',
          options: this.deleteYnOptions,
          itemTitle: 'title',
          itemValue: 'value',
        },
      ]
    },
    tableColumns() {
      return [
        { key: 'no', title: '번호', align: 'center', width: '90px' },
        { key: 'name', title: '이름', align: 'center' },
        { key: 'email', title: '이메일', align: 'center' },
        { key: 'phone', title: '연락처', align: 'center' },
        { key: 'authYn', title: '인증여부', align: 'center', width: '110px' },
        { key: 'loginType', title: '가입유형', align: 'center' },
        { key: 'branchName', title: '추천지점', align: 'center' },
        { key: 'insertDt', title: '가입일', align: 'center' },
        { key: 'deleteYn', title: '상태', align: 'center', width: '90px' },
      ]
    },
  },

  methods: {
    /**
     * 서버에서 회원 목록을 조회한다.
     * 검색 조건 + 페이지네이션 파라미터를 전달하고 결과를 items / total에 세팅한다.
     */
    async fetchList() {
      this.loading = true
      try {
        const res = await getMemberList({
          keyword:   this.search.keyword   || undefined,
          loginType: this.search.loginType || undefined,
          deleteYn:  this.search.deleteYn  || undefined,
          page:      this.page,
          size:      this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
        }
      } catch (e) {
        console.error('[MemberListPage] fetchList error:', e)
      } finally {
        this.loading = false
      }
    },

    /** 검색 버튼 클릭: 페이지를 1로 리셋 후 조회 */
    handleSearch() {
      this.page = 1
      this.fetchList()
    },

    /** 초기화 버튼 클릭: 검색 조건 + 페이지 초기화 후 재조회 */
    handleReset() {
      this.search = { keyword: '', loginType: '', deleteYn: '' }
      this.page = 1
      this.fetchList()
    },

    /** 페이지 변경 (AppPagination emit) */
    onPageChange(newPage) {
      this.page = newPage
      this.fetchList()
    },

    /** 페이지당 항목 수 변경 (AppPagination emit) */
    onSizeChange(newSize) {
      this.size = newSize
      this.page = 1
      this.fetchList()
    },

    /** 행 클릭 → 회원 상세 페이지로 이동 */
    goToDetail(item) {
      const row = item?.raw || item
      if (!row?.userCode) return
      this.$router.push(`/members/${row.userCode}`)
    },

    /** 회원 등록 완료 후 목록 새로고침 */
    onMemberCreated() {
      this.createDialog = false
      this.page = 1
      this.fetchList()
    },

    /** 탈퇴여부 칩 색상 반환 */
    getStatusColor(deleteYn) {
      return deleteYn === 'Y' ? 'grey' : 'success'
    },

    /** 탈퇴여부 칩 레이블 반환 */
    getStatusLabel(deleteYn) {
      return deleteYn === 'Y' ? '탈퇴' : '정상'
    },

    /** 가입유형 표시: 사이트 / 소셜 provider 목록 조합 */
    getLoginTypeSummary(item) {
      const parts = []
      if (item.siteLoginYn === 'Y') parts.push('사이트')
      if (item.oauthProviders?.length > 0) {
        item.oauthProviders.forEach(p => {
          parts.push(this.providerLabelMap[p] || p)
        })
      }
      return parts.length > 0 ? parts.join(' · ') : '-'
    },
  },
}
</script>

<template>
  <AdminShell title="회원관리">
    <template #header-actions>
      <v-btn color="primary" prepend-icon="mdi-account-plus-outline" @click="createDialog = true">
        회원 등록
      </v-btn>
    </template>

    <p class="admin-page-subtitle">가입 회원 조회 및 정보 관리</p>

    <AdminFilterBar
      :model-value="search"
      :fields="filterFields"
      @update:model-value="search = $event"
      @search="handleSearch"
      @reset="handleReset"
    />

    <div class="admin-page-card pa-4 mt-5">
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="text-subtitle-1 font-weight-bold">
          회원 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}명</v-chip>
        </div>
      </div>

      <AdminDataTable
        :columns="tableColumns"
        :items="items"
        row-key="userCode"
        :loading="loading"
        empty-text="검색 결과가 없습니다."
        @row-click="goToDetail"
      >
        <template #cell-no="{ index }">
          <span class="text-caption text-medium-emphasis">
            {{ total - (page - 1) * size - index }}
          </span>
        </template>

        <template #cell-name="{ item }">
          <span class="font-weight-medium">{{ (item?.raw || item)?.name || '-' }}</span>
        </template>

        <template #cell-email="{ item }">
          <span class="text-body-2">{{ (item?.raw || item)?.email || '-' }}</span>
        </template>

        <template #cell-phone="{ item }">
          <span class="text-body-2">{{ (item?.raw || item)?.phone || '-' }}</span>
        </template>

        <template #cell-authYn="{ item }">
          <AdminStatusBadge
            :status="(item?.raw || item)?.authYn === 'Y' ? 'VERIFIED' : 'UNVERIFIED'"
            :map="{
              VERIFIED: { label: '인증', tone: 'success' },
              UNVERIFIED: { label: '미인증', tone: 'warning' },
            }"
            size="x-small"
          />
        </template>

        <template #cell-loginType="{ item }">
          <div class="d-flex align-center ga-1 flex-wrap justify-center">
            <v-chip
              v-if="(item?.raw || item)?.siteLoginYn === 'Y'"
              size="x-small"
              color="primary"
              variant="tonal"
              label
            >
              사이트
            </v-chip>
            <v-chip
              v-for="provider in ((item?.raw || item)?.oauthProviders || [])"
              :key="provider"
              size="x-small"
              :color="providerColorMap[provider] || 'default'"
              variant="tonal"
              label
            >
              {{ providerLabelMap[provider] || provider }}
            </v-chip>
            <span
              v-if="(item?.raw || item)?.siteLoginYn !== 'Y' && (!((item?.raw || item)?.oauthProviders || []).length)"
              class="text-caption text-medium-emphasis"
            >-</span>
          </div>
        </template>

        <template #cell-branchName="{ item }">
          <span class="text-body-2">{{ (item?.raw || item)?.branchName || '-' }}</span>
        </template>

        <template #cell-insertDt="{ item }">
          <span class="text-caption">
            {{ (item?.raw || item)?.insertDt ? (item?.raw || item).insertDt.substring(0, 10) : '-' }}
          </span>
        </template>

        <template #cell-deleteYn="{ item }">
          <AdminStatusBadge
            :status="(item?.raw || item)?.deleteYn === 'Y' ? 'WITHDRAWN' : 'ACTIVE'"
            :map="{
              ACTIVE: { label: '정상', tone: 'success' },
              WITHDRAWN: { label: '탈퇴', tone: 'default' },
            }"
            size="x-small"
          />
        </template>

        <template #loading>
          <div class="pa-6">
            <AdminLoadingState text="회원 목록을 불러오는 중..." />
          </div>
        </template>

        <template #empty>
          <div class="pa-4">
            <AdminEmptyState title="검색 결과가 없습니다." />
          </div>
        </template>
      </AdminDataTable>

      <div class="mt-4">
        <AppPagination
          :total="total"
          :page="page"
          :size="size"
          @update:page="onPageChange"
          @update:size="onSizeChange"
        />
      </div>
    </div>

    <MemberCreateDialog v-model="createDialog" @created="onMemberCreated" />
  </AdminShell>
</template>
